// Part groups --- a file each part has its own of (a sleeve's print artwork) for every part of a group at once: one PDF
// with a page per part (the sleeves exported together), or a file per part (Affinity's Export Slices, an artboard each),
// dropped on the Group section or in the dialog. Each page or file is matched to its part (matchFiles in
// shared/part-groups.ts) and shown as a picture beside it to check, a pick changing it; Upload puts each into its part's
// draft spec (a page as a PDF of its own, web/src/lib/pdf-split.ts) the way a drop on the part's own row does
// (uploadNeed), then Release for all… releases them. pdf.js and pdf-lib load only when a PDF is dropped or split.
import { AlertTriangle, Check, FileText, Loader2, Rocket, Upload as UploadIcon } from "lucide-react";
import { type DragEvent, useEffect, useRef, useState } from "react";
import type { Item, PartGroup } from "../../../shared/db.gen.ts";
import { type FileCandidate, type MatchBy, matchFiles, memberLabel } from "../../../shared/part-groups.ts";
import { type FileNeed, packagingOf } from "../../../shared/projects.ts";
import type { SpecFileKind } from "../../../shared/specs.ts";
import { plural } from "../lib/format.ts";
import { can } from "../lib/mutate.ts";
import type { PdfPage, ReadPdf } from "../lib/pdf-read.ts";
import { Badge, Button, cx, Select } from "../ui/controls.tsx";
import { toast } from "../ui/toast.tsx";
import { Modal, memberFile } from "./PartGroups.tsx";
import { bytes, needSlot, uploadNeed, useSpecIndex, useUploads } from "./specs/lib.ts";

/** The print file each part of the group has its own of (the artwork, not the shared dieline); null when there's none. */
export function perPartNeed(group: Pick<PartGroup, "shared_files">, item: Item): FileNeed | null {
  return packagingOf(item)?.files.find((n) => !n.kinds.some((k) => group.shared_files.includes(k))) ?? null;
}

export const hasFiles = (e: DragEvent) => [...e.dataTransfer.types].includes("Files");

type Source = { kind: "pdf"; file: File; pages: PdfPage[]; pdf: ReadPdf } | { kind: "files"; files: File[] };
type Outcome = "uploading" | "uploaded" | "unchanged" | { error: string };

const isPdf = (f: File) => f.type === "application/pdf" || /\.pdf$/i.test(f.name);
const stem = (name: string) => name.replace(/\.[^.]+$/, "");
const THUMB = 64;
const BIG = 560;

/** A picture of a page or file, its longer side `size` px: a PDF's page, an image as it is, a PDF file's first page. */
async function picture(source: Source, i: number, size: number): Promise<string | null> {
  if (source.kind === "pdf") return source.pdf.thumb(i, size);
  const file = source.files[i];
  if (/^image\/(png|jpeg|webp|gif)$/.test(file.type)) return URL.createObjectURL(file);
  if (!isPdf(file)) return null;
  const { readPdf } = await import("../lib/pdf-read.ts");
  const pdf = await readPdf(file);
  try {
    return await pdf.thumb(0, size);
  } finally {
    pdf.close();
  }
}
const forget = (url: string | null | undefined) => url?.startsWith("blob:") && URL.revokeObjectURL(url);

export function GroupFilesDialog({
  group,
  members,
  need,
  initial,
  onClose,
  onRelease,
}: {
  group: PartGroup;
  /** The group's current parts, in the Group section's order. */
  members: Item[];
  need: FileNeed;
  /** Files dropped on the Group section. */
  initial?: File[];
  onClose: () => void;
  onRelease: () => void;
}) {
  const ix = useSpecIndex();
  const uploads = useUploads();
  const picker = useRef<HTMLInputElement>(null);
  const [source, setSource] = useState<Source | null>(null);
  const [reading, setReading] = useState<string | null>(null);
  const [picks, setPicks] = useState<(number | null)[]>([]);
  const [by, setBy] = useState<MatchBy[]>([]); // how the picks were found
  const [thumbs, setThumbs] = useState<Map<number, string>>(new Map());
  // A page or file shown big, to read what the small picture can't ("2" or "2S")
  const [zoom, setZoom] = useState<{ index: number; url: string | null } | null>(null);
  const [over, setOver] = useState(false);
  const [outcomes, setOutcomes] = useState<Map<string, Outcome>>(new Map());
  const [phase, setPhase] = useState<"pick" | "uploading" | "done">("pick");
  const kind = need.kinds[0] as SpecFileKind;
  const what = need.label.toLowerCase();

  /** Reads what was dropped: one PDF of several pages is split by page, anything else is a file per part. */
  const take = async (files: File[]) => {
    if (!files.length || phase !== "pick") return;
    setReading(files.length === 1 ? files[0].name : plural(files.length, "file"));
    try {
      let next: Source = { kind: "files", files };
      let candidates: FileCandidate[] = files.map((f) => ({ name: stem(f.name) }));
      if (files.length === 1 && isPdf(files[0])) {
        const { readPdf } = await import("../lib/pdf-read.ts");
        const pdf = await readPdf(files[0]);
        if (pdf.pages.length > 1) {
          next = { kind: "pdf", file: files[0], pages: pdf.pages, pdf };
          candidates = pdf.pages.map((p) => ({ name: [stem(files[0].name), p.label, ...p.titles].join(" "), text: p.text }));
        } else pdf.close();
      }
      const matched = matchFiles(group, members, candidates, next.kind === "pdf");
      setSource(next); // the one before closes as this one comes in (below)
      setPicks(matched.map((m) => m.index));
      setBy([...new Set(matched.flatMap((m) => m.by ?? []))]);
      setThumbs(new Map());
      setZoom(null);
    } catch (e) {
      toast.error(`Couldn't read ${files.length === 1 ? files[0].name : "the files"}: ${(e as Error).message}`);
    } finally {
      setReading(null);
    }
  };

  // The dropped files, once; the PDF closed when the dialog goes.
  const started = useRef(false);
  useEffect(() => {
    if (initial?.length && !started.current) {
      started.current = true;
      take(initial);
    }
  });
  useEffect(() => () => (source?.kind === "pdf" ? source.pdf.close() : undefined), [source]);

  // Pictures to check by, one at a time: a PDF's pages, or each file (an image as it is, a PDF's first page).
  useEffect(() => {
    if (!source) return;
    let stop = false;
    const urls: string[] = [];
    (async () => {
      const count = source.kind === "pdf" ? source.pages.length : source.files.length;
      for (let i = 0; i < count && !stop; i++) {
        const url = await picture(source, i, THUMB).catch(() => null);
        if (url) urls.push(url);
        if (url && !stop) setThumbs((t) => new Map(t).set(i, url));
      }
    })();
    return () => {
      stop = true;
      for (const u of urls) forget(u);
    };
  }, [source]);
  const enlarge = (index: number) => {
    if (!source) return;
    setZoom({ index, url: null });
    picture(source, index, BIG)
      .catch(() => null)
      .then((url) =>
        setZoom((z) => {
          if (z?.index === index && !z.url) return { index, url };
          forget(url); // closed (or another one opened) meanwhile
          return z;
        }),
      );
  };
  const shrink = () => {
    forget(zoom?.url);
    setZoom(null);
  };

  const count = source ? (source.kind === "pdf" ? source.pages.length : source.files.length) : 0;
  const optionLabel = (i: number) => {
    if (source?.kind !== "pdf") return source?.files[i].name ?? "";
    const p = source.pages[i];
    const named = [p.titles[0], p.label && p.label !== String(i + 1) ? p.label : ""].find(Boolean);
    return `Page ${i + 1}${named ? ` · ${named}` : ""}`;
  };
  const options = [
    { value: "", label: `Nothing (its ${what} stays as it is)` },
    ...Array.from({ length: count }, (_, i) => ({ value: String(i), label: optionLabel(i) })),
  ];
  const chosen = members.map((m, i) => ({ m, pick: picks[i] ?? null })).filter((c): c is { m: Item; pick: number } => c.pick !== null);
  const unused = Array.from({ length: count }, (_, i) => i).filter((i) => !picks.includes(i));
  const twice = (i: number) => picks.filter((p) => p === i).length > 1;
  const order = by.includes("order");
  const byText = [by.includes("sku") && "their SKUs", by.includes("values") && `their ${group.varies_by.join(" and ").toLowerCase()}`]
    .filter(Boolean)
    .join(" or ");

  const upload = async () => {
    if (!source || !chosen.length || phase !== "pick") return;
    setPhase("uploading");
    let files: File[];
    try {
      if (source.kind === "pdf") {
        const { splitPdf } = await import("../lib/pdf-split.ts");
        const parts = await splitPdf(
          await source.file.arrayBuffer(),
          chosen.map((c) => c.pick),
        );
        files = parts.map(
          (b, k) => new File([b as Uint8Array<ArrayBuffer>], `${chosen[k].m.sku || stem(source.file.name)} ${what}.pdf`, { type: "application/pdf" }),
        );
      } else files = chosen.map((c) => source.files[c.pick]);
    } catch (e) {
      toast.error(`Couldn't split ${source.kind === "pdf" ? source.file.name : "the files"}: ${(e as Error).message}`);
      setPhase("pick");
      return;
    }
    const set = (id: string, o: Outcome) => setOutcomes((m) => new Map(m).set(id, o));
    for (const [k, { m }] of chosen.entries()) {
      set(m.id, "uploading");
      try {
        const r = await uploadNeed(ix, m.id, need, files[k]);
        set(m.id, r ? (r.unchanged ? "unchanged" : "uploaded") : { error: "Its spec couldn't take the file" });
      } catch (e) {
        set(m.id, { error: (e as Error).message });
      }
    }
    setPhase("done");
  };

  const done = [...outcomes.values()];
  const uploaded = done.filter((o) => o === "uploaded").length;
  const same = done.filter((o) => o === "unchanged").length;
  const failed = done.filter((o) => typeof o === "object").length;

  const zone = (
    <div
      className={cx(
        "flex flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-6 text-center",
        over ? "border-accent bg-accent-soft" : "border-line-strong",
      )}
    >
      {reading ? (
        <span className="flex items-center gap-2 text-muted">
          <Loader2 className="size-4 animate-spin" /> Reading {reading}…
        </span>
      ) : (
        <>
          <span className="font-medium">Drop one PDF with a page for each part, or a file for each part</span>
          <span className="max-w-[420px] text-[12px] text-muted">
            Each page or file goes to the part it names (its SKU or its {group.varies_by.join(" and ").toLowerCase()}), or in page order. You check each one
            before anything uploads.
          </span>
          <Button size="sm" icon={<UploadIcon className="size-3.5" />} onClick={() => picker.current?.click()}>
            Browse…
          </Button>
        </>
      )}
    </div>
  );

  return (
    <Modal
      title={`${need.label} for every part of ${group.name}`}
      width={640}
      onClose={() => phase !== "uploading" && onClose()}
      onSubmit={() => (phase === "done" ? onClose() : upload())}
      footer={
        phase === "done" ? (
          <>
            <span className="mr-auto text-[12px] text-muted">
              {[uploaded && `${plural(uploaded, "part")} got the new ${what}`, same && `${same} the same as before`, failed && `${failed} failed`]
                .filter(Boolean)
                .join(" · ")}
            </span>
            {uploaded > 0 && can("change_notice.release") && (
              <Button icon={<Rocket className="size-3.5" />} onClick={onRelease}>
                Release for all…
              </Button>
            )}
            <Button variant="primary" type="submit" autoFocus>
              Close
            </Button>
          </>
        ) : (
          <>
            {source && phase === "pick" && (
              <Button variant="ghost" className="mr-auto" onClick={() => picker.current?.click()}>
                Other files…
              </Button>
            )}
            <Button variant="ghost" disabled={phase === "uploading"} onClick={onClose}>
              Cancel
            </Button>
            {source && (
              <Button variant="primary" type="submit" disabled={!chosen.length || phase !== "pick"} kbd="↵">
                {phase === "uploading" ? "Uploading…" : `Upload to ${plural(chosen.length, "part")}`}
              </Button>
            )}
          </>
        )
      }
    >
      <div
        className="relative"
        onDragOver={(e) => {
          if (!hasFiles(e) || phase !== "pick") return;
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false);
        }}
        onDrop={(e) => {
          setOver(false);
          if (!e.dataTransfer.files.length) return;
          e.preventDefault();
          take([...e.dataTransfer.files]);
        }}
      >
        {!source ? (
          zone
        ) : (
          <>
            <div className={cx("mb-2 flex items-start gap-2 rounded-md px-2.5 py-1.5 text-[12px]", order ? "bg-warn-soft text-warn" : "bg-surface-2")}>
              {order && <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />}
              <span className="min-w-0 flex-1">
                {source.kind === "pdf" ? `${source.file.name}: ${plural(count, "page")}` : plural(count, "file")}
                {order
                  ? `. Nothing on the pages names a part, so they went to the parts in page order: check each picture.`
                  : byText
                    ? `, matched by ${byText}: check each picture.`
                    : `. Nothing names a part: pick each one's ${source.kind === "pdf" ? "page" : "file"}.`}
                {unused.length > 0 &&
                  ` ${unused.length === 1 ? optionLabel(unused[0]) : plural(unused.length, source.kind === "pdf" ? "page" : "file")} going to no part.`}
              </span>
              {reading && <Loader2 className="size-3.5 shrink-0 animate-spin" />}
            </div>
            <div className="flex max-h-[min(60vh,560px)] flex-col gap-1 overflow-y-auto">
              {members.map((m, i) => {
                const pick = picks[i] ?? null;
                const mine = memberFile(ix, m.id, kind);
                const outcome = outcomes.get(m.id);
                const spec = ix.specByItem.get(m.id);
                const slot = spec ? needSlot(ix.filesBySpec.get(spec.id) ?? [], need.kinds) : undefined;
                const progress = outcome === "uploading" && slot ? uploads.get(slot.id) : undefined;
                const thumb = pick !== null ? thumbs.get(pick) : undefined;
                return (
                  <div key={m.id} className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 rounded-md border border-line px-2 py-1.5">
                    <button
                      type="button"
                      disabled={pick === null}
                      onClick={() => pick !== null && enlarge(pick)}
                      title={pick !== null ? `${optionLabel(pick)}: show it bigger` : undefined}
                      className="flex shrink-0 cursor-zoom-in items-center justify-center rounded bg-surface-2 disabled:cursor-default"
                      style={{ width: THUMB, height: THUMB }}
                    >
                      {thumb ? (
                        <img src={thumb} alt="" className="max-h-full max-w-full object-contain" />
                      ) : pick !== null ? (
                        <FileText className="size-5 text-faint" />
                      ) : null}
                    </button>
                    <div className="min-w-[140px] flex-1">
                      <div className="truncate font-medium">{memberLabel(group, m) || m.name}</div>
                      <div className="flex items-center gap-1.5 text-[12px] text-muted">
                        <span className="truncate font-mono">{m.sku}</span>
                        {mine.draft ? (
                          mine.draft === mine.released ? (
                            <Badge tone="ok">Released ✓</Badge>
                          ) : (
                            <Badge tone="accent">Not released</Badge>
                          )
                        ) : (
                          <Badge tone="warn">None yet</Badge>
                        )}
                      </div>
                      {pick !== null && twice(pick) && <div className="text-[11px] text-warn">Picked for another part too</div>}
                    </div>
                    <div className="w-full sm:w-[220px] sm:shrink-0">
                      {phase === "pick" ? (
                        <Select
                          value={pick === null ? "" : String(pick)}
                          options={options}
                          aria-label={`${memberLabel(group, m) || m.sku}'s ${what}`}
                          onChange={(e) =>
                            setPicks((p) => members.map((_, j) => (j === i ? (e.target.value === "" ? null : Number(e.target.value)) : (p[j] ?? null))))
                          }
                        />
                      ) : (
                        <div className="flex items-center justify-end gap-1.5 text-[12px]">
                          {outcome === "uploading" ? (
                            <span className="flex items-center gap-1.5 text-muted">
                              <Loader2 className="size-3.5 animate-spin" />
                              {progress ? `${Math.floor((progress.loaded / Math.max(1, progress.total)) * 100)}% of ${bytes(progress.total)}` : "Uploading…"}
                            </span>
                          ) : outcome === "uploaded" ? (
                            <span className="flex items-center gap-1 text-ok">
                              <Check className="size-3.5" /> Uploaded
                            </span>
                          ) : outcome === "unchanged" ? (
                            <span className="text-muted">Same as before</span>
                          ) : typeof outcome === "object" ? (
                            <span className="truncate text-danger" title={outcome.error}>
                              {outcome.error}
                            </span>
                          ) : pick === null ? (
                            <span className="text-faint">Left as it is</span>
                          ) : (
                            <span className="text-faint">Waiting</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {phase === "done" && uploaded > 0 && (
              <div className="mt-2 text-[12px] text-muted">They're in each part's draft spec: vendors get them once they're released.</div>
            )}
          </>
        )}
        {zoom && (
          <button
            type="button"
            onClick={shrink}
            className="absolute inset-0 z-10 flex cursor-zoom-out flex-col items-center justify-center gap-2 rounded-lg bg-surface/95 p-2"
          >
            {zoom.url ? (
              <img src={zoom.url} alt="" className="max-h-[calc(100%-28px)] max-w-full object-contain shadow-pop" />
            ) : (
              <Loader2 className="size-5 animate-spin text-muted" />
            )}
            <span className="text-[12px] text-muted">{optionLabel(zoom.index)} · click to close</span>
          </button>
        )}
      </div>
      <input
        ref={picker}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          take([...(e.target.files ?? [])]);
          e.target.value = "";
        }}
      />
    </Modal>
  );
}
